import type { PersistentState } from '~/types/persistent'
import { UserRole } from '~/types/account'

/**
 * Parse a persistent query value: JSON when it parses, the raw value otherwise.
 * Used by the persistent plugin for its generic keys; it makes no promise about
 * the shape of the result (a JSON scalar comes back as the scalar). The
 * session-gated list params have their own reader, parseQueryList.
 */
export const parseQueryValue = (raw: unknown): unknown => {
  if (typeof raw !== 'string') {
    return raw
  }
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/** The non-empty string values of a query parameter, repeated or single. */
export const readQueryStrings = (raw: unknown): string[] =>
  [raw].flat().filter((value): value is string => typeof value === 'string' && !!value)

/** A single non-empty string value of a query parameter, or null. */
export const readQueryString = (raw: unknown): string | null =>
  typeof raw === 'string' && raw ? raw : null

/**
 * Read a list-valued query parameter (?enabledFeature=, ?hybridRoute=):
 * repeated parameters, a bare value, a JSON string ("paylater") or a JSON array
 * (["a","b"]). Returns null when the query carries no value, and an empty array
 * for a supplied empty list (?enabledFeature=[]), which clears the persisted value.
 */
export const parseQueryList = (raw: unknown): string[] | null => {
  const values = readQueryStrings(raw)
  if (!values.length) {
    return null
  }
  return values
    .flatMap((value): unknown[] => {
      const parsed = parseQueryValue(value)
      if (Array.isArray(parsed)) { return parsed }
      return [typeof parsed === 'string' ? parsed : value]
    })
    .filter((feature): feature is string => typeof feature === 'string' && !!feature)
}

export type SessionGate = { role: UserRole | undefined, isProduction: boolean }

// The URL may set these for agents only.
const agentOnly = ({ role }: SessionGate) => role === UserRole.Agent
// The URL may set these for anyone outside production; in production, agents only.
const outsideProductionOrAgent = ({ role, isProduction }: SessionGate) => !isProduction || role === UserRole.Agent

type SessionGatedParam = {
  key: keyof PersistentState
  read: (raw: unknown) => string | string[] | null
  allowed: (gate: SessionGate) => boolean
}

/**
 * Persistent params the persistent plugin never takes from the URL: init-auth
 * takes them once the session is known, each behind its gate, and carries them
 * on the redirects that end a request before then. A key missing here is
 * persisted from any visitor's URL. AB#65034
 */
export const SESSION_GATED_PARAMS: SessionGatedParam[] = [
  // Re-prices the cart and checkout as of another date; agents only, as nuxt2
  // does (plugins/persistent.ts:50-53). AB#64453
  { key: 'viewAsDate', read: readQueryString, allowed: agentOnly },
  // Switches on unlaunched payment methods and beta flags. AB#64760
  { key: 'enabledFeature', read: parseQueryList, allowed: outsideProductionOrAgent },
  // The agent header's date for the demonstrator reports API. AB#65034
  { key: 'demoViewAsDate', read: readQueryString, allowed: agentOnly },
  // Moves in-app navigation to Nuxt 4 for routes not yet turned on, the dev and
  // QA testing flow; the guard splits each entry on commas. AB#65034
  { key: 'hybridRoute', read: parseQueryList, allowed: outsideProductionOrAgent }
]

// Browsers cap a cookie's lifetime at 400 days, so no session outlasts that.
export const MAX_SESSION_SECONDS = 400 * 24 * 60 * 60

/**
 * The persisted expirationDialogTime (seconds since the epoch) is used only when
 * it is a finite time between now and the longest a session can last. Anything
 * else, such as NaN, a string or a past time, reads as no expiry. AB#64760
 */
export const validSessionExpiry = (value: unknown, nowMs = Date.now()): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  const nowSeconds = nowMs / 1000
  return value > nowSeconds && value <= nowSeconds + MAX_SESSION_SECONDS ? value : null
}
